import { cookieName, locales, type Locale } from '$lib/paraglide/runtime';

/**
 * Top-level path segments of the logged-in application area. Inside it the
 * UI language follows the user's preference (PARAGLIDE_LOCALE cookie backed
 * by users.preferred_language), NOT the URL: app links are unprefixed, so a
 * URL-derived locale would reset the language to English on every navigation
 * (SAM-836). Marketing routes stay URL-driven for SEO (SAM-688).
 */
const APP_AREA_SEGMENTS = new Set(['app', 'settings', 'dashboard', 'category']);

/**
 * True when the pathname points into the logged-in app area, with or without
 * a locale prefix (`/app/...` and `/pl/app/...` both qualify — prefixed URLs
 * appear transiently right after a locale switch).
 */
export function isAppAreaPath(pathname: string): boolean {
	const segments = pathname.split('/').filter(Boolean);
	const head = (locales as readonly string[]).includes(segments[0] ?? '')
		? segments[1]
		: segments[0];
	return head !== undefined && APP_AREA_SEGMENTS.has(head);
}

/**
 * Extract the locale from a `Cookie` request header (server side — the
 * runtime's `extractLocaleFromCookie` only works with `document.cookie`).
 * Returns undefined for a missing cookie or an unsupported locale.
 */
export function localeFromCookieHeader(header: string | null): Locale | undefined {
	if (!header) return undefined;
	const value = header.match(new RegExp(`(?:^|;\\s*)${cookieName}=([^;]+)`))?.[1];
	return value !== undefined && (locales as readonly string[]).includes(value)
		? (value as Locale)
		: undefined;
}
